if (!window.DigitalizModules) window.DigitalizModules = {};
if (!window.DigitalizModules.ProductCard) {
  const _variantCache = new Map();      // handle -> variants[]
  const _pendingCallbacks = new Map();  // handle -> [callbacks]

  function _notifyHandle(handle, variants) {
    (_pendingCallbacks.get(handle) || []).forEach(cb => cb(variants));
    _pendingCallbacks.delete(handle);
  }

  function _onVariantsReady(handle, cb) {
    if (_variantCache.has(handle)) {
      cb(_variantCache.get(handle));
      return;
    }
    if (!_pendingCallbacks.has(handle)) _pendingCallbacks.set(handle, []);
    _pendingCallbacks.get(handle).push(cb);
  }

  function _fetchHandles(handles) {
    handles
      .filter(h => !_variantCache.has(h))
      .forEach(handle => {
        fetch(`/products/${encodeURIComponent(handle)}.js`)
          .then(r => r.json())
          .then(data => {
            const variants = data.variants.map(v => ({
              id: v.id,
              price: v.price,
              available: v.available,
              option1: v.option1,
              option2: v.option2,
              option3: v.option3,
              image: v.featured_image ? v.featured_image.src : null,
            }));
            variants.forEach(v => {
              if (v.image) (new Image()).src = v.image;
            });
            _variantCache.set(handle, variants);
            _notifyHandle(handle, variants);
          })
          .catch(() => {
            _variantCache.set(handle, []);
            _notifyHandle(handle, []);
          });
      });
  }

  function _collectAndFetch(root) {
    const handles = [
      ...new Set(
        [...(root || document).querySelectorAll('[data-product-card][data-product-handle]')]
          .map(el => el.dataset.productHandle)
          .filter(Boolean)
      ),
    ];
    if (handles.length) _fetchHandles(handles);
  }

  if (document.readyState === 'complete') {
    _collectAndFetch(document);
  } else {
    window.addEventListener('load', () => _collectAndFetch(document), { once: true });
  }

  class ProductCard {
    constructor(el) {
      this.el = el;
      // Back-reference so other modules can read the resolved variant directly
      // (e.g. when the change event fires synchronously, before they listen).
      el.__digitalizCard = this;
      this.handle = el.dataset.productHandle || '';
      this.variants = [];
      this.selectedOptions = {};
      this.activeVariant = null;
      this.defaultVariantId = null;
      this.linkEl = this.el.querySelector('[data-pc-link]');
      this.baseHref = this.linkEl ? this.linkEl.getAttribute('href') : null;
      this.leadTimes = this._readLeadTimes();
      this.deliveryEl = this.el.querySelector('[data-pc-delivery]');
      this.deliveryTextEl = this.el.querySelector('[data-pc-delivery-text]');
      this._bindEvents();
      if (this.handle) {
        _onVariantsReady(this.handle, variants => {
          this.variants = variants;
          // Resolve to the default variant (first available, else first) — the
          // same selection the snippet pre-rendered — so the price, lead time
          // and activeVariant are backed by real variant data from the start.
          this._applyDefaultVariant();
        });
      }
    }

    _bindEvents() {
      this.el.querySelectorAll('[data-pc-option-btn]').forEach(btn => {
        btn.addEventListener('click', () => this._onOptionClick(btn));
      });
    }

    _onOptionClick(btn) {
      const optionIdx = parseInt(btn.dataset.pcOptionBtn, 10);
      const value = btn.dataset.value;
      const group = this.el.querySelector(`[data-pc-option="${optionIdx}"]`);

      group.querySelectorAll('[data-pc-option-btn]').forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      this.selectedOptions[optionIdx] = value;

      if (group.dataset.optionType === 'color') {
        this._updateImage(optionIdx, value);
      }
      this._updatePrice();
      this._updateLeadTime();
      this._updateLink();
    }

    // Point the card link at the shopper's chosen variant so the product page
    // opens on it. The default (page-load) variant keeps the clean product URL —
    // better for SEO, and the product page resolves to that variant anyway.
    _updateLink() {
      if (!this.linkEl || !this.baseHref) return;
      const variant = this.activeVariant;
      if (!variant || variant.id === this.defaultVariantId) {
        this.linkEl.setAttribute('href', this.baseHref);
        return;
      }
      const url = new URL(this.baseHref, window.location.origin);
      url.searchParams.set('variant', variant.id);
      this.linkEl.setAttribute('href', url.pathname + url.search + url.hash);
    }

    _matchingVariants() {
      const entries = Object.entries(this.selectedOptions);
      if (!entries.length) return this.variants;
      return this.variants.filter(v => {
        for (const [idx, val] of entries) {
          if (v[`option${parseInt(idx, 10) + 1}`] !== val) return false;
        }
        return true;
      });
    }

    _updatePrice() {
      const priceEl = this.el.querySelector('[data-pc-price]');
      const fromEl = this.el.querySelector('[data-pc-from]');
      if (!priceEl) return;

      const matches = this._matchingVariants();
      if (!matches.length) {
        this.activeVariant = null;
        this._emitChange();
        return;
      }

      const prices = matches.map(v => v.price);
      const minPrice = Math.min(...prices);
      const hasMultiplePrices = new Set(prices).size > 1;

      if (Number.isFinite(minPrice)) window.Digitaliz?.setCurrency?.(priceEl, minPrice);

      if (fromEl) fromEl.classList.toggle('is-hidden', !hasMultiplePrices);

      // Resolved variant for consumers that add to cart / total prices:
      // the first available match, falling back to the first match.
      this.activeVariant = matches.find(v => v.available) || matches[0] || null;
      this._emitChange();
    }

    _emitChange() {
      this.el.dispatchEvent(new CustomEvent('digitaliz:product-card:change', {
        bubbles: true,
        detail: { handle: this.handle, variant: this.activeVariant }
      }));
    }

    _applyDefaultVariant() {
      const variant = this.variants.find(v => v.available) || this.variants[0];
      if (!variant) {
        this._updatePrice();
        return;
      }
      this.defaultVariantId = variant.id;
      // Highlight the option buttons matching the default variant, mirroring a
      // manual selection. _updatePrice() then resolves the active variant. The
      // image is left as rendered: the snippet already shows this variant's.
      this.el.querySelectorAll('[data-pc-option]').forEach(group => {
        const idx = parseInt(group.dataset.pcOption, 10);
        const value = variant[`option${idx + 1}`];
        const buttons = [...group.querySelectorAll('[data-pc-option-btn]')];
        const btn = buttons.find(b => b.dataset.value === value);
        if (!btn) return;
        buttons.forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.selectedOptions[idx] = value;
      });
      this._updatePrice();
      this._updateLeadTime();
    }

    _updateImage(optionIdx, value) {
      const key = `option${optionIdx + 1}`;
      const variant = this.variants.find(v => v[key] === value && v.image);
      if (!variant) return;
      const img = this.el.querySelector('[data-pc-image]');
      if (!img) return;
      img.srcset = '';
      img.src = variant.image;
    }

    _readLeadTimes() {
      const node = this.el.querySelector('[data-pc-lead-times]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    _updateLeadTime() {
      if (!this.leadTimes || !this.deliveryEl || !this.deliveryTextEl) return;

      // Variant-specific lead time only when a single variant is fully resolved;
      // otherwise fall back to the product-level value.
      const matches = this._matchingVariants();
      let text = this.leadTimes.fallback || '';
      if (matches.length === 1) {
        const variantLead = this.leadTimes.variants[matches[0].id];
        if (variantLead) text = variantLead;
      }

      this.deliveryTextEl.textContent = text;
      this.deliveryEl.classList.toggle('is-hidden', text.trim() === '');
    }
  }

  window.DigitalizModules.ProductCard = ProductCard;

  // Initialize cards inside a subtree added to the DOM after page load
  // (e.g. async-loaded product recommendations). Fetches variant data and
  // binds interactions, scoped to root so existing cards are untouched.
  ProductCard.initWithin = function (root) {
    if (!root) return;
    _collectAndFetch(root);
    initCards(root);
  };

  function initCards(root) {
    (root || document).querySelectorAll('[data-product-card]').forEach(el => {
      // Skip cards already initialised — lets a container holding both existing
      // and freshly appended cards be re-scanned without double-binding events.
      if (el.__digitalizCard) return;
      new ProductCard(el);
    });
  }

  initCards(document);

  document.addEventListener('shopify:section:load', event => {
    _collectAndFetch(event.target);
    initCards(event.target);
  });
}
